import { CONNECTORS_UI_PATH } from '#pplx/frontend/apps/ask/connectors/consts/connectors.js';
import { useCachedPluginEntitiesEnabledVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useCachedPluginEntitiesEnabledVariation.js';
import { usePluginEntitiesEnabledVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/usePluginEntitiesEnabledVariation.js';
import { useEarlyAccess } from '#pplx/frontend/apps/ask/rbac/hooks/useEarlyAccess.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
const LEGACY_HREFS = {
    connectors: CONNECTORS_UI_PATH,
    skills: '/computer/skills',
    manageSkills: '/computer/skills',
};
const PLUGINS_HREFS = {
    connectors: '/computer/plugins',
    skills: '/computer/plugins?kind=skills',
    manageSkills: '/computer/plugins?tab=installed&kind=skills',
};
// Logged-out visitors have no Eppo subject. Set `enabled` to true to release Plugins to them.
const LOGGED_OUT_PLUGINS = { enabled: false, loading: false };
// The pinned assignment, further gated by Plugins early access for white-glove org members.
function usePluginEntitiesEnabledForSkip(skip) {
    const enabled = useCachedPluginEntitiesEnabledVariation({ defaultValue: false, skip });
    const { access } = useEarlyAccess('plugins', {
        reason: 'usePluginEntitiesEnabled',
        skip: skip || !enabled,
        pinned: true,
    });
    const read = !skip && enabled;
    return { enabled: read && access === 'allowed', loading: read && access === 'loading' };
}
const PLUGINS_OFF = { enabled: false, loading: false };
/**
 * Whether the Plugins surface is on for this member. By default pins the last cached assignment
 * for the page so navigation can't flip while Eppo loads or reconciles; live changes apply on the
 * next load. Plugins early access reads the org and the member's permissions, so the pinned
 * answer is `loading` until they resolve. `live` reads the current assignment and reports loading
 * instead. `skip` reads nothing and reports Plugins off.
 */
export function usePluginEntitiesEnabled({ live = false, skip = false, } = {}) {
    const { status } = useSession();
    const loggedOut = status === 'unauthenticated';
    // These shells still register only the legacy skill and connector routes.
    const supportsPlugins = !import.meta.env.VITE_IS_COMPUTER_APP_BUILD &&
        !import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD;
    const pinned = usePluginEntitiesEnabledForSkip(skip || live || !supportsPlugins || status !== 'authenticated');
    const current = usePluginEntitiesEnabledVariation({
        defaultValue: false,
        skip: skip || !live || loggedOut,
    });
    const earlyAccess = useEarlyAccess('plugins', {
        reason: 'usePluginEntitiesEnabled',
        skip: skip || !live || loggedOut || current.loading || !current.variation,
    });
    if (skip)
        return PLUGINS_OFF;
    if (loggedOut)
        return LOGGED_OUT_PLUGINS;
    if (live) {
        return {
            enabled: current.variation && earlyAccess.access === 'allowed',
            loading: current.loading || earlyAccess.access === 'loading',
        };
    }
    return pinned;
}
export function useConnectorActionPageHref() {
    const { enabled } = usePluginEntitiesEnabled();
    return enabled ? PLUGINS_HREFS.connectors : CONNECTORS_UI_PATH;
}
export function usePluginsNavigationHrefs() {
    const { status } = useSession();
    // The Computer app shell still registers only the legacy skill and connector routes.
    const skip = !!import.meta.env.VITE_IS_COMPUTER_APP_BUILD || status !== 'authenticated';
    const { enabled } = usePluginEntitiesEnabledForSkip(skip);
    return enabled ? PLUGINS_HREFS : LEGACY_HREFS;
}
//# sourceMappingURL=usePluginsNavigationHrefs.js.map